/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  Grid,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Snackbar,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import LocationOptionList from '../../../main/widgets/LocationOptionList';
import StatusOptionList from '../../../main/widgets/StatusOptionList';
import TrackingEventTypeOptionList from '../../../main/widgets/TrackingEventTypeOptionList';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import { usePermissions } from '../../../main/hooks/usePermissions';
import PermissionGate from '../../../main/components/PermissionGate';
import {
  createShipmentPiece,
  extractShipmentPieceFieldErrors,
  previewChargeableWeightKg,
  previewVolumetricWeightKg,
} from '../../../main/API/shipmentPiece';
import { translateAxiosErrorMessage } from '../../../main/utils/masterApiError';
import { toLocationWritePk } from '../../../main/utils/locationDisplayName';
import OmrSymbol from '../../../main/components/OmrSymbol';
import { useTrackingEventPrompt } from '../../../main/hooks/useTrackingEventPrompt';
import {
  applyTrackingEventControl,
  buildPieceTrackingSnapshot,
} from '../../../main/utils/trackingEventControl';

const EMPTY_FORM = {
  shipment: '',
  description: '',
  weight_kg: '',
  width_cm: '',
  length_cm: '',
  height_cm: '',
  volumetric_weight_kg: '',
  status: '',
  event_type: '',
  note: '',
  product_category_code: '',
  meter_price_override: '',
  extra_cost: '',
};

const DIM_FIELDS = [
  { key: 'width_cm', label: 'W', placeholder: 'W' },
  { key: 'length_cm', label: 'L', placeholder: 'L' },
  { key: 'height_cm', label: 'H', placeholder: 'H' },
];

/**
 * Modal that creates a single `ShipmentPiece` via POST `/api/shipment_piece/`.
 *
 * Two usage modes:
 *
 *  1. Manager screen — pass `lockedShipmentId={null}`, the modal asks the user
 *     to type the parent shipment id.
 *  2. Inside a specific shipment's edit screen — pass `lockedShipmentId={id}`,
 *     the modal hides the shipment-id field and creates pieces for that parent.
 *
 * `onCreated(piece)` fires after a successful POST so the parent can re-fetch
 * the shipment / piece list. The new piece's `human_label` reflects the *new*
 * total count — siblings need a re-fetch (see FRONTEND_INTEGRATION_GUIDE FAQ).
 *
 * Validation surfaced inline (per the rollup-release docs):
 *  - dimensions: W/L/H must be all-or-none, rendered under the dim block.
 *  - per-field non-positive errors (weight_kg, width_cm, …).
 *  - sequence: collision message rendered next to the auto-assigned label.
 */
export default function CreateShipmentPieceModal({
  open,
  onClose,
  onCreated,
  lockedShipmentId = null,
}) {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const { promptIfNeeded, trackingPromptModal } = useTrackingEventPrompt();
  const canCreate = hasPermission(PERMISSIONS.SHIPMENT_PIECES_CREATE);
  const canListEventTypes = hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST);

  const [form, setForm] = useState(EMPTY_FORM);
  const [eventTypeOpt, setEventTypeOpt] = useState(null);
  const [location, setLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', color: 'success' });
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm({
      ...EMPTY_FORM,
      shipment: lockedShipmentId != null ? String(lockedShipmentId) : '',
    });
    setLocation(null);
    setEventTypeOpt(null);
    setFieldErrors({});
  }, [open, lockedShipmentId]);

  const showSnack = useCallback((message, color = 'success') => {
    setSnack({ open: true, message, color });
  }, []);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key] && !prev.dimensions) return prev;
      const next = { ...prev };
      delete next[key];
      if (key === 'width_cm' || key === 'length_cm' || key === 'height_cm') {
        delete next.dimensions;
      }
      return next;
    });
  };

  const trimmedShipmentId = String(form.shipment || '').trim();
  const shipmentIdValid = /^\d+$/.test(trimmedShipmentId) && Number(trimmedShipmentId) > 0;
  const canSubmit = useMemo(
    () => canCreate && shipmentIdValid && !saving,
    [canCreate, shipmentIdValid, saving]
  );

  const dimsFilled = useMemo(() => {
    return [form.width_cm, form.length_cm, form.height_cm]
      .filter((v) => v !== '' && v != null).length;
  }, [form.width_cm, form.length_cm, form.height_cm]);

  const volumetricPreview = useMemo(() => {
    if (dimsFilled !== 3) return null;
    return previewVolumetricWeightKg(form.width_cm, form.length_cm, form.height_cm);
  }, [dimsFilled, form.width_cm, form.length_cm, form.height_cm]);

  const chargeablePreview = useMemo(() => {
    return previewChargeableWeightKg(
      form.weight_kg,
      form.volumetric_weight_kg !== '' ? form.volumetric_weight_kg : volumetricPreview
    );
  }, [form.weight_kg, form.volumetric_weight_kg, volumetricPreview]);

  const handleSave = useCallback(async () => {
    if (!canSubmit) return;
    setSaving(true);
    setFieldErrors({});
    try {
      const trackingDecision = await promptIfNeeded({
        isCreate: true,
        current: buildPieceTrackingSnapshot(form, location),
        entityType: 'shipment_piece',
      });
      if (trackingDecision === null) {
        return;
      }
      const numKeys = ['weight_kg', 'width_cm', 'length_cm', 'height_cm', 'volumetric_weight_kg', 'meter_price_override', 'extra_cost'];
      let payload = { shipment: Number(trimmedShipmentId) };
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'shipment') return;
        if (v === '' || v == null) return;
        if (numKeys.includes(k)) payload[k] = v;
        else payload[k] = v;
      });
      if (location) {
        const locPk = toLocationWritePk(location);
        if (locPk != null) payload.location = locPk;
      }
      payload = applyTrackingEventControl(payload, trackingDecision);
      const res = await createShipmentPiece(payload);
      const piece = res && res.data;
      showSnack(t('shipmentPieces.createdSuccess', { defaultValue: 'Piece created' }));
      if (typeof onCreated === 'function') onCreated(piece);
      setTimeout(() => {
        if (typeof onClose === 'function') onClose();
      }, 250);
    } catch (e) {
      const errs = extractShipmentPieceFieldErrors(e);
      if (Object.keys(errs).length) setFieldErrors(errs);
      showSnack(
        translateAxiosErrorMessage(e, t)
          || t('shipmentPieces.errorLoad'),
        'danger'
      );
    } finally {
      setSaving(false);
    }
  }, [canSubmit, trimmedShipmentId, form, location, promptIfNeeded, showSnack, t, onCreated, onClose]);

  return (
    <>
      {trackingPromptModal}
      <Modal open={open} onClose={saving ? undefined : onClose}>
        <ModalDialog
          variant="outlined"
          sx={{
            width: { xs: '100%', sm: 620 },
            maxWidth: '100%',
            maxHeight: { xs: '100dvh', sm: '90vh' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            overflowX: 'hidden',
            boxSizing: 'border-box',
            borderRadius: { xs: 0, sm: '12px' },
            p: 0,
          }}
        >
          <Box sx={{ px: 2, pt: 2, pb: 1.5, flexShrink: 0 }}>
            <ModalClose onClick={saving ? undefined : onClose} />
            <DialogTitle sx={{ p: 0, mb: 0.5 }}>
              {t('shipmentPieces.createTitle', { defaultValue: 'New piece' })}
            </DialogTitle>
            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
              {t('shipmentPieces.createHint', {
                defaultValue: 'Barcode + human label are generated automatically.',
              })}
            </Typography>
          </Box>

          <Divider />

          <DialogContent
            sx={{
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              px: 2,
              py: 2,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Stack spacing={2} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
              <Sheet variant="soft" sx={{ p: 2, borderRadius: 'lg', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', overflow: 'hidden' }}>
                <Typography level="body-xs" sx={{ mb: 1.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary' }}>
                  {t('shipmentPieces.sectionBasics', { defaultValue: 'Basics' })}
                </Typography>
                <Grid container spacing={1.5} sx={{ width: '100%', maxWidth: '100%', m: 0 }}>
                  <Grid xs={12} sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                      {t('shipmentPieces.shipmentCol', { defaultValue: 'Shipment' })} *
                    </Typography>
                    <Input
                      size="sm"
                      sx={{ width: '100%' }}
                      value={form.shipment}
                      onChange={(e) => setField('shipment', e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="#"
                      disabled={Boolean(lockedShipmentId) || saving}
                      color={form.shipment && !shipmentIdValid ? 'danger' : 'neutral'}
                    />
                    {fieldErrors.sequence ? (
                      <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>
                        {fieldErrors.sequence}
                      </FormHelperText>
                    ) : null}
                  </Grid>

                  <Grid xs={12} sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                      {t('actions.description')}
                    </Typography>
                    <Textarea
                      minRows={2}
                      size="sm"
                      sx={{ width: '100%' }}
                      value={form.description}
                      onChange={(e) => setField('description', e.target.value)}
                      disabled={saving}
                    />
                  </Grid>
                </Grid>
              </Sheet>

              <Sheet variant="soft" sx={{ p: 2, borderRadius: 'lg', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', overflow: 'hidden' }}>
                <Typography level="body-xs" sx={{ mb: 1.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary' }}>
                  {t('shipmentPieces.sectionWeightDimensions', { defaultValue: 'Weight & dimensions' })}
                </Typography>
                <Grid container spacing={1.5} sx={{ width: '100%', maxWidth: '100%', m: 0 }}>
                  <Grid xs={12} sm={4} sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                      {t('shipmentPieces.weight')}
                    </Typography>
                    <Input
                      size="sm"
                      type="number"
                      inputMode="decimal"
                      sx={{ width: '100%' }}
                      value={form.weight_kg}
                      onChange={(e) => setField('weight_kg', e.target.value)}
                      disabled={saving}
                      endDecorator="kg"
                      color={fieldErrors.weight_kg ? 'danger' : 'neutral'}
                      slotProps={{ input: { min: 0, step: '0.001' } }}
                    />
                    {fieldErrors.weight_kg ? (
                      <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>
                        {fieldErrors.weight_kg}
                      </FormHelperText>
                    ) : null}
                  </Grid>

                  <Grid xs={12} sm={8} sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                      {t('shipmentPieces.dimensions')}
                    </Typography>
                    <Sheet variant="outlined" sx={{ p: 1, borderRadius: 'md', bgcolor: 'background.surface', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', overflow: 'hidden' }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
                        {DIM_FIELDS.map(({ key, label, placeholder }) => (
                          <Box key={key} sx={{ minWidth: 0 }}>
                            <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.tertiary', textAlign: 'center' }}>
                              {label}
                            </Typography>
                            <Input
                              size="sm"
                              type="number"
                              inputMode="decimal"
                              sx={{ width: '100%' }}
                              value={form[key]}
                              onChange={(e) => setField(key, e.target.value)}
                              placeholder={placeholder}
                              disabled={saving}
                              color={fieldErrors[key] || fieldErrors.dimensions ? 'danger' : 'neutral'}
                              slotProps={{ input: { min: 0, step: '0.001' } }}
                            />
                          </Box>
                        ))}
                      </Box>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.75, textAlign: 'center' }}>
                        cm
                      </Typography>
                    </Sheet>
                    {fieldErrors.dimensions ? (
                      <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>
                        {fieldErrors.dimensions}
                      </FormHelperText>
                    ) : null}
                    {dimsFilled > 0 && dimsFilled < 3 && !fieldErrors.dimensions ? (
                      <FormHelperText sx={{ color: 'warning.700', mt: 0.5 }}>
                        {t('shipmentPieces.dimensionsAllOrNoneHint', {
                          defaultValue: 'Fill all three of W/L/H, or leave them all empty.',
                        })}
                      </FormHelperText>
                    ) : null}
                  </Grid>

                  <Grid xs={12} sm={6} sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                      {t('shipmentPieces.volumetricWeightOverride', {
                        defaultValue: 'Volumetric weight (kg, optional)',
                      })}
                    </Typography>
                    <Input
                      size="sm"
                      type="number"
                      inputMode="decimal"
                      sx={{ width: '100%' }}
                      value={form.volumetric_weight_kg}
                      onChange={(e) => setField('volumetric_weight_kg', e.target.value)}
                      placeholder={volumetricPreview != null ? `auto ≈ ${volumetricPreview.toFixed(3)}` : ''}
                      disabled={saving}
                      color={fieldErrors.volumetric_weight_kg ? 'danger' : 'neutral'}
                    />
                    <FormHelperText sx={{ color: 'text.tertiary', mt: 0.5 }}>
                      {t('shipmentPieces.volumetricAutoHint', {
                        defaultValue: 'Leave empty to let the server compute from W*L*H.',
                      })}
                    </FormHelperText>
                  </Grid>

                  <Grid xs={12} sm={6} sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                      {t('shipmentPieces.chargeableWeight', {
                        defaultValue: 'Chargeable weight',
                      })}
                    </Typography>
                    <Input
                      size="sm"
                      readOnly
                      sx={{ width: '100%' }}
                      value={chargeablePreview != null ? `${chargeablePreview.toFixed(3)} kg` : '—'}
                    />
                    {chargeablePreview != null ? (
                      <Box sx={{ mt: 0.75 }}>
                        <Chip size="sm" variant="soft" color="neutral" sx={{ maxWidth: '100%', whiteSpace: 'normal', height: 'auto' }}>
                          {t('shipmentPieces.chargeablePreviewHint', {
                            defaultValue: 'Preview only — server returns the authoritative value.',
                          })}
                        </Chip>
                      </Box>
                    ) : (
                      <FormHelperText sx={{ color: 'text.tertiary', mt: 0.5 }}>
                        {t('shipmentPieces.chargeablePreviewHint', {
                          defaultValue: 'Preview only — server returns the authoritative value.',
                        })}
                      </FormHelperText>
                    )}
                  </Grid>
                </Grid>
              </Sheet>

              <Sheet variant="soft" sx={{ p: 2, borderRadius: 'lg', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', overflow: 'hidden' }}>
                <Typography level="body-xs" sx={{ mb: 1.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary' }}>
                  {t('shipmentPieces.sectionTrackingPricing', { defaultValue: 'Tracking & pricing' })}
                </Typography>
                <Stack spacing={1.5}>
                  <StatusOptionList
                    status={form.status}
                    setStatus={(v) => setField('status', v || '')}
                    allowEmpty
                    disabled={saving}
                    label={t('shipmentPieces.status')}
                  />

                  {canListEventTypes && (
                    <TrackingEventTypeOptionList
                      label={t('shipmentPieces.eventType')}
                      required={false}
                      disabled={saving}
                      defaultValue={eventTypeOpt}
                      onChange={(_, v) => {
                        setEventTypeOpt(v ?? null);
                        setField('event_type', (v && v.code) || '');
                      }}
                    />
                  )}

                  <LocationOptionList
                    defaultValue={location}
                    onChange={(_, v) => setLocation(v ?? null)}
                    disabled={saving}
                    label={t('shipmentPieces.location')}
                  />

                  <Grid container spacing={1.5} sx={{ width: '100%', maxWidth: '100%', m: 0 }}>
                    <Grid xs={12} sm={6} sx={{ minWidth: 0 }}>
                      <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                        {t('shipmentPieces.productCategoryCode', {
                          defaultValue: 'Product category code (per-piece)',
                        })}
                      </Typography>
                      <Input
                        size="sm"
                        sx={{ width: '100%' }}
                        value={form.product_category_code}
                        onChange={(e) => setField('product_category_code', e.target.value)}
                        disabled={saving}
                        placeholder={t('shipmentPieces.productCategoryCodePlaceholder', {
                          defaultValue: 'Overrides shipment value',
                        })}
                        color={fieldErrors.product_category_code ? 'danger' : 'neutral'}
                      />
                      {fieldErrors.product_category_code ? (
                        <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>
                          {fieldErrors.product_category_code}
                        </FormHelperText>
                      ) : null}
                    </Grid>
                    <Grid xs={12} sm={6} sx={{ minWidth: 0 }}>
                      <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                        {t('shipmentPieces.meterPriceOverride', {
                          defaultValue: 'Meter price override (per-piece)',
                        })}
                      </Typography>
                      <Input
                        size="sm"
                        type="number"
                        inputMode="decimal"
                        sx={{ width: '100%' }}
                        value={form.meter_price_override}
                        onChange={(e) => setField('meter_price_override', e.target.value)}
                        disabled={saving}
                        endDecorator={<OmrSymbol height={14} />}
                        color={fieldErrors.meter_price_override ? 'danger' : 'neutral'}
                      />
                      {fieldErrors.meter_price_override ? (
                        <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>
                          {fieldErrors.meter_price_override}
                        </FormHelperText>
                      ) : null}
                    </Grid>
                    <Grid xs={12} sx={{ minWidth: 0 }}>
                      <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                        {t('shipmentPieces.extraCost', { defaultValue: 'Extra cost (per-piece)' })}
                      </Typography>
                      <Input
                        size="sm"
                        type="number"
                        inputMode="decimal"
                        sx={{ width: '100%' }}
                        value={form.extra_cost}
                        onChange={(e) => setField('extra_cost', e.target.value)}
                        disabled={saving}
                        endDecorator={<OmrSymbol height={14} />}
                        color={fieldErrors.extra_cost ? 'danger' : 'neutral'}
                      />
                      {fieldErrors.extra_cost ? (
                        <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>
                          {fieldErrors.extra_cost}
                        </FormHelperText>
                      ) : null}
                    </Grid>
                  </Grid>
                </Stack>
              </Sheet>

              <Sheet variant="soft" sx={{ p: 2, borderRadius: 'lg', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', overflow: 'hidden' }}>
                <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                  {t('actions.notes')}
                </Typography>
                <Textarea
                  minRows={2}
                  size="sm"
                  sx={{ width: '100%' }}
                  value={form.note}
                  onChange={(e) => setField('note', e.target.value)}
                  disabled={saving}
                />
              </Sheet>

              {Object.keys(fieldErrors).length > 0 ? (
                <Alert variant="soft" color="danger">
                  {t('shipmentPieces.fixErrorsAbove', {
                    defaultValue: 'Please fix the highlighted fields and try again.',
                  })}
                </Alert>
              ) : null}
            </Stack>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ px: 2, py: 1.5, flexShrink: 0, bgcolor: 'background.level1' }}>
            <Button variant="plain" color="neutral" onClick={onClose} disabled={saving}>
              {t('actions.cancel')}
            </Button>
            <PermissionGate permission={PERMISSIONS.SHIPMENT_PIECES_CREATE}>
              <Button color="primary" onClick={handleSave} loading={saving} disabled={!canSubmit}>
                {t('actions.create', { defaultValue: 'Create' })}
              </Button>
            </PermissionGate>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Snackbar
        autoHideDuration={3000}
        open={snack.open}
        variant="soft"
        color={snack.color}
        onClose={(_, reason) => { if (reason !== 'clickaway') setSnack((s) => ({ ...s, open: false })); }}
      >
        {snack.message}
      </Snackbar>
    </>
  );
}
